import { Theme } from "@emotion/react";
import { mdsCommonTheme } from "../common";
import { mdsHarvestThemeColors } from "@/mds/themes/harvest/harvest";

export const mdsHarvestMoonThemeColors = {
  ink: {
    0: "#D6D5E6",
    100: "#A5A3D0",
    200: "#7977BA",
    300: "#413F8E",
    400: "#100F31",
  },
  inkDark: {
    0: "#11111A",
    50: "#1A1925",
    100: "#20202F",
    150: "#272638",
    200: "#2E2D41",
    300: "#3C3B54",
    400: "#4C4B67",
    500: "#504F6D",
    600: "#89889F",
    700: "#D6D6E6",
  },
  graphite: {
    0: "#262626",
    100: "#2E2E2E",
    200: "#353535",
    300: "#3C3C3C",
    400: "#525252",
    500: "#616161",
    600: "#7F7F7F",
    700: "#9C9C9C",
    800: "#B9B9B9",
    900: "#D7D7D7",
  },
  canvas: {
    0: "#FCF8F1",
    100: "#F9F1E4",
  },
  blueprint: {
    0: "#191930",
    100: "#1E2242",
    200: "#23284C",
    300: "#2A305A",
    400: "#303768",
    // These are not correct
    500: "#3D69D9",
    600: "#1D47B3",
  },
  latte: {
    0: "#F9F1E2",
    100: "#F1E7D3",
    200: "#E8DCC4",
  },
  editor: {
    0: "#FFE8E6",
    100: "#FFB5AB",
    200: "#FF8170",
    300: "#FF5C45",
    400: "#FF361A",
    500: "#DB1A00",
  },
  editorDark: {
    0: "#3F1A1F",
    100: "#5F272F",
    200: "#8E404B",
    300: "#B8626F",
    400: "#D9818E",
  },
  success: "#23B688",
  transparentCanvas: "rgba(252, 248, 241, 0.25)",
  transparentInk: "rgba(39, 38, 56, 0.5)",
};

export const mdsThemeHarvestMoon: Theme = {
  ...mdsCommonTheme,

  colors: {
    ...mdsCommonTheme.colors,
    ...mdsHarvestMoonThemeColors,

    text: {
      contrast: mdsHarvestMoonThemeColors.inkDark[700],
      danger: mdsHarvestMoonThemeColors.editor[400],
      primary: mdsHarvestMoonThemeColors.graphite[900],
      secondary: mdsHarvestMoonThemeColors.graphite[800],
      tertiary: mdsHarvestMoonThemeColors.inkDark[600],
      onAccent: mdsHarvestMoonThemeColors.inkDark[0],
      link: {
        default: mdsHarvestThemeColors.blueprint[400],
        hover: mdsHarvestThemeColors.blueprint[300],
      },
      emphasis: mdsHarvestThemeColors.blueprint[300],
      positive: mdsHarvestMoonThemeColors.success,
      negative: mdsHarvestMoonThemeColors.editor[400],
      editor: {
        metadata: mdsHarvestMoonThemeColors.graphite[800],
      },
    },
    icon: {
      primary: mdsHarvestMoonThemeColors.graphite[800],
      secondary: mdsHarvestMoonThemeColors.graphite[700],
      placeholder: mdsHarvestMoonThemeColors.inkDark[600],
      onAccent: mdsHarvestMoonThemeColors.graphite[0],
      brand: mdsHarvestMoonThemeColors.editor[300],
      selected: mdsHarvestThemeColors.blueprint[300],
      emphasis: mdsHarvestThemeColors.blueprint[300],
      danger: mdsHarvestMoonThemeColors.editor[400],
      onChromeInactive: mdsHarvestMoonThemeColors.inkDark[500],
    },
    surface: {
      chrome: mdsHarvestMoonThemeColors.inkDark[150],
      pane: mdsHarvestMoonThemeColors.inkDark[0],
      paneHeader: mdsHarvestMoonThemeColors.inkDark[50],
      headsUp: {
        default: mdsHarvestMoonThemeColors.inkDark[50],
        hover: mdsHarvestMoonThemeColors.inkDark[100],
      },
      chat: mdsHarvestMoonThemeColors.inkDark[100],
      menu: mdsHarvestMoonThemeColors.inkDark[50],
      modal: mdsHarvestMoonThemeColors.inkDark[50],
      popover: mdsHarvestMoonThemeColors.inkDark[50],
      toast: mdsHarvestMoonThemeColors.inkDark[50],
      preview: mdsHarvestMoonThemeColors.inkDark[50],
      previewHeader: mdsHarvestMoonThemeColors.inkDark[200],
      tooltip: mdsHarvestMoonThemeColors.inkDark[700],
      overlay: {
        default: "rgba(0, 0, 0, 0.5)",
        onboarding: mdsHarvestMoonThemeColors.transparentInk,
      },
      onboarding: mdsHarvestMoonThemeColors.inkDark[150],
      onboardingSubtle: mdsHarvestMoonThemeColors.inkDark[100],
    },
    border: {
      input: {
        default: {
          default: mdsHarvestMoonThemeColors.inkDark[400],
          focused: mdsHarvestMoonThemeColors.inkDark[500],
          danger: mdsHarvestMoonThemeColors.editor[400],
        },
        brand: {
          default: mdsCommonTheme.colors.transparent,
          focused: mdsHarvestMoonThemeColors.editor[300],
        },
        emphasis: {
          default: mdsHarvestMoonThemeColors.ink[400],
          focused: mdsHarvestMoonThemeColors.blueprint[200],
        },
      },
      button: {
        marketing: {
          default: mdsHarvestMoonThemeColors.inkDark[700],
          hover: mdsHarvestMoonThemeColors.editor[300],
        },
      },
      default: mdsHarvestMoonThemeColors.inkDark[200],
      hover: mdsHarvestMoonThemeColors.inkDark[300],
      onAccent: mdsHarvestMoonThemeColors.inkDark[300],
      chrome: mdsHarvestMoonThemeColors.inkDark[150],
      emphasis: mdsHarvestThemeColors.blueprint[200],
      surface: mdsHarvestMoonThemeColors.inkDark[100],
      ghost: mdsHarvestMoonThemeColors.inkDark[0],
    },
    background: {
      voice: {
        self: mdsHarvestMoonThemeColors.inkDark[100],
        others: mdsHarvestMoonThemeColors.inkDark[150],
      },
      button: {
        contrast: {
          default: mdsHarvestMoonThemeColors.graphite[900],
          hover: mdsHarvestMoonThemeColors.graphite[800],
        },
        default: {
          default: mdsHarvestMoonThemeColors.inkDark[150],
          hover: mdsHarvestMoonThemeColors.inkDark[200],
        },
        chip: {
          default: mdsHarvestMoonThemeColors.inkDark[150],
          hover: mdsHarvestMoonThemeColors.inkDark[200],
          selectedNegative: mdsHarvestMoonThemeColors.editor[0],
          selectedPositive: "#FFFFFF",
        },
        toggle: {
          default: mdsHarvestMoonThemeColors.inkDark[150],
          hover: mdsHarvestMoonThemeColors.inkDark[200],
          toggled: mdsHarvestMoonThemeColors.blueprint[100],
          toggledHover: mdsHarvestMoonThemeColors.blueprint[200],
        },
        onAccent: {
          default: "transparent",
          hover: mdsHarvestMoonThemeColors.inkDark[300],
        },
        emphasis: {
          default: mdsHarvestThemeColors.blueprint[300],
          hover: mdsHarvestThemeColors.blueprint[400],
        },
        emphasisLight: {
          default: mdsHarvestMoonThemeColors.blueprint[100],
          hover: mdsHarvestMoonThemeColors.blueprint[200],
        },
        transparent: {
          default: "transparent",
          hover: mdsHarvestMoonThemeColors.inkDark[100],
        },
        navigation: {
          default: "transparent",
          hover: mdsHarvestMoonThemeColors.inkDark[100],
        },
        chrome: {
          default: mdsHarvestMoonThemeColors.inkDark[50],
          hover: mdsHarvestMoonThemeColors.inkDark[100],
        },
        subtle: {
          default: mdsHarvestMoonThemeColors.inkDark[50],
          hover: mdsHarvestMoonThemeColors.inkDark[100],
        },
        danger: {
          default: "transparent",
          hover: mdsHarvestMoonThemeColors.editorDark[0],
        },
        dangerFilled: {
          default: mdsHarvestMoonThemeColors.editorDark[0],
          hover: mdsHarvestMoonThemeColors.editorDark[100],
        },
        brand: {
          default: mdsHarvestMoonThemeColors.editor[300],
          hover: mdsHarvestMoonThemeColors.editor[200],
        },
        tabBar: {
          active: mdsHarvestMoonThemeColors.inkDark[0],
          default: mdsHarvestMoonThemeColors.inkDark[50],
          hover: mdsHarvestMoonThemeColors.inkDark[100],
        },
        onChrome: {
          default: "transparent",
          hover: mdsHarvestMoonThemeColors.inkDark[200],
        },
        floatingIcon: {
          default: mdsHarvestMoonThemeColors.inkDark[0],
          hover: mdsHarvestMoonThemeColors.inkDark[100],
        },
        contrastAccent: {
          default: mdsHarvestMoonThemeColors.inkDark[0],
          hover: mdsHarvestMoonThemeColors.inkDark[50],
        },
      },
      list: {
        default: mdsHarvestMoonThemeColors.inkDark[0],
        hover: mdsHarvestMoonThemeColors.inkDark[50],
        selected: mdsHarvestMoonThemeColors.inkDark[100],
        deepsearch: mdsHarvestMoonThemeColors.blueprint[0],
      },
      menu: {
        default: mdsHarvestMoonThemeColors.inkDark[50],
        hover: mdsHarvestMoonThemeColors.inkDark[100],
        selected: mdsHarvestMoonThemeColors.inkDark[200],
        emphasized: mdsHarvestMoonThemeColors.blueprint[0],
      },
      input: {
        default: {
          default: mdsHarvestMoonThemeColors.inkDark[0],
          focused: mdsHarvestMoonThemeColors.inkDark[100],
        },
        chrome: {
          default: mdsHarvestMoonThemeColors.inkDark[50],
          focused: mdsHarvestMoonThemeColors.inkDark[100],
          hover: mdsHarvestMoonThemeColors.inkDark[100],
        },
        onAccent: {
          default: mdsHarvestMoonThemeColors.inkDark[200],
          focused: mdsHarvestMoonThemeColors.inkDark[50],
        },
        inline: {
          default: "transparent",
          focused: mdsHarvestMoonThemeColors.inkDark[200],
          hover: mdsHarvestMoonThemeColors.inkDark[200],
        },
      },
      message: {
        user: mdsHarvestMoonThemeColors.inkDark[50],
        mem: "transparent",
      },
      badge: {
        default: mdsHarvestMoonThemeColors.inkDark[200],
        emphasis: mdsHarvestMoonThemeColors.blueprint[100],
        moreEmphasis: mdsHarvestMoonThemeColors.blueprint[300],
        negative: mdsHarvestMoonThemeColors.editor[0],
      },
      banner: {
        danger: mdsHarvestMoonThemeColors.editor[0],
        emphasis: mdsHarvestMoonThemeColors.blueprint[0],
        default: mdsHarvestMoonThemeColors.inkDark[200],
      },
      card: {
        emphasis: mdsHarvestMoonThemeColors.blueprint[200],
        default: mdsHarvestMoonThemeColors.inkDark[100],
      },
      activeTab: mdsHarvestThemeColors.blueprint[300],
      illustration: {
        default: mdsHarvestMoonThemeColors.inkDark[300],
        onAccent: mdsHarvestMoonThemeColors.inkDark[300],
        contrast: mdsHarvestMoonThemeColors.inkDark[0],
      },
      diff: mdsHarvestMoonThemeColors.blueprint[0],
      skeleton: {
        bg: "#20202f", // deliberately using non-themed color
        ends: mdsHarvestThemeColors.inkDark[100],
        middle: mdsHarvestThemeColors.inkDark[50],
      },
    },
  },

  shadows: {
    ...mdsCommonTheme.shadows,
    surface: "0 4px 6px 0 rgba(17, 17, 26, 0.75)",
  },
};
